// Builds product-value bins with the current limit L as an exact boundary.
const STEPS = [25, 50, 100];
const FALLBACK = [10, 25, 50, 100, 200, 250, 500, 1000];

const edgesFor = (s, L, cap) => {
  const e = [0];
  if (L > 0) {
    const below = [];
    for (let x = L - s; x >= s / 2; x -= s) below.unshift(x);
    e.push(...below, L);
    for (let x = L + s; x <= cap; x += s) e.push(x);
  } else {
    for (let x = s; x <= cap; x += s) e.push(x);
  }
  return e;
};

export const buildBins = (orders, L) => {
  const vals = orders.map((o) => o[0]).sort((a, b) => a - b);
  const p95 = vals[Math.min(vals.length - 1, Math.floor(vals.length * 0.95))] || 0;
  const lim = L > 0 && Number.isFinite(L) ? L : null;
  const cap = lim ? Math.max(p95, lim + 1) : p95;
  let step = STEPS.find((s) => { const n = edgesFor(s, lim, cap).length; return n >= 8 && n <= 14; });
  if (!step) step = FALLBACK.reduce((b, s) => (Math.abs(edgesFor(s, lim, cap).length - 11) < Math.abs(edgesFor(b, lim, cap).length - 11) ? s : b));
  const edges = edgesFor(step, lim, cap);
  const bins = edges.map((from, i) => ({ from, to: edges[i + 1] ?? null, count: 0, gratis: 0 }));
  for (const o of orders) {
    let i = bins.length - 1;
    while (i > 0 && o[0] < bins[i].from) i -= 1;
    bins[i].count += 1;
    if (o[2]) bins[i].gratis += 1;
  }
  bins.forEach((b) => {
    const mid = b.to == null ? b.from : (b.from + b.to) / 2;
    b.cat = !lim ? 'all' : b.from >= lim ? 'above' : mid >= lim * 0.8 ? 'near' : 'below';
  });
  return { bins, step, limitIdx: lim ? edges.indexOf(lim) : -1 };
};

// Horizontal position (0..1) of a product value over equal-width bands.
export const posOf = (bins, step, x) => {
  const n = bins.length;
  let i = bins.length - 1;
  while (i > 0 && x < bins[i].from) i -= 1;
  const b = bins[i];
  const w = (b.to ?? b.from + step) - b.from;
  return Math.min(1, (i + Math.min(1, (x - b.from) / w)) / n);
};

export const conclusion = (bins, limitIdx) => {
  if (limitIdx <= 0) return null;
  const up = bins[limitIdx];
  const down = bins[limitIdx - 1];
  const total = bins.reduce((s, b) => s + b.count, 0);
  const belowAll = bins.slice(0, limitIdx).reduce((s, b) => s + b.count, 0);
  const base = { up, down, belowPct: total ? (belowAll / total) * 100 : 0, abovePct: total ? ((total - belowAll) / total) * 100 : 0 };
  if (!up.count && !down.count) return { ...base, kind: 'flat', pct: 0 };
  if (!down.count) return { ...base, kind: 'more', pct: null };
  const d = ((up.count - down.count) / down.count) * 100;
  return { ...base, pct: d, kind: d >= 20 ? 'more' : d > -20 ? 'flat' : 'less' };
};

export const niceTicks = (max) => {
  if (max <= 0) return [0, 1];
  const raw = max / 4;
  const p = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * p).find((s) => s >= raw);
  const top = Math.ceil(max / step) * step;
  const t = [];
  for (let v = 0; v <= top + 1e-9; v += step) t.push(Math.round(v));
  return t;
};
